import React from 'react';

/**
 * Tiny inline SVG sparkline chart. Zero dependencies.
 * Props:
 *  - data: [{ date, impressions, clicks }, ...]
 *  - width, height (default 160x40)
 *  - dualSeries: true → draws both impressions (blue) and clicks (emerald)
 */
export default function Sparkline({ data = [], width = 180, height = 44, dualSeries = true }) {
  if (!data || data.length === 0) {
    return <span className="text-xs text-gray-400">Nessun dato</span>;
  }

  const imp = data.map(d => d.impressions || 0);
  const clk = data.map(d => d.clicks || 0);
  const maxImp = Math.max(1, ...imp);
  const maxClk = Math.max(1, ...clk);

  const step = data.length > 1 ? width / (data.length - 1) : 0;

  const pathFor = (arr, max) => {
    return arr.map((v, i) => {
      const x = i * step;
      const y = height - (v / max) * (height - 4) - 2;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  };

  const impPath = pathFor(imp, maxImp);
  const clkPath = dualSeries ? pathFor(clk, maxClk) : '';

  // Area fill under impressions line
  const impArea = `${impPath} L${width},${height} L0,${height} Z`;

  const totalImp = imp.reduce((a, b) => a + b, 0);
  const totalClk = clk.reduce((a, b) => a + b, 0);
  const ctr = totalImp > 0 ? ((totalClk / totalImp) * 100).toFixed(2) : '0.00';

  return (
    <div className="inline-flex flex-col" data-testid="sparkline">
      <svg width={width} height={height} className="block" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <path d={impArea} fill="rgba(59, 130, 246, 0.12)" />
        <path d={impPath} fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {dualSeries && (
          <path d={clkPath} fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="0" />
        )}
        {/* Dot on last point for impressions */}
        {imp.length > 0 && (
          <circle
            cx={(imp.length - 1) * step}
            cy={height - (imp[imp.length - 1] / maxImp) * (height - 4) - 2}
            r="2"
            fill="#3b82f6"
          />
        )}
      </svg>
      <div className="mt-1 flex items-center gap-2 text-[10px] leading-tight">
        <span className="inline-flex items-center gap-1 text-blue-600">
          <span className="w-2 h-0.5 bg-blue-500 inline-block" /> {totalImp} imp
        </span>
        <span className="inline-flex items-center gap-1 text-emerald-600">
          <span className="w-2 h-0.5 bg-emerald-500 inline-block" /> {totalClk} click
        </span>
        <span className="text-gray-500">· {ctr}% CTR</span>
      </div>
    </div>
  );
}
